import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View, Platform, Switch } from 'react-native';
import { ActivityIndicator, Checkbox, Text, TextInput as PaperTextInput } from 'react-native-paper';
import { useAppColors } from '../../hooks/useAppColors';
import { useRegister } from '../../hooks/useAuth';
import { getApiError, weightToKg, heightToCm } from '../../utils/formatters';
import { isAdult } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import AuthLayout from './AuthLayout';
import DatePicker from '../../components/DatePicker';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';
import AuthDivider from './AuthDivider';

export default function RegisterScreen({ navigation }: any) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const registerMutation = useRegister();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // All registering users are 'member' role (no more guardian/athlete distinction)
  const role = 'member';
  const [secureEntry, setSecureEntry] = useState(true);
  const [secureEntryConfirm, setSecureEntryConfirm] = useState(true);
  const [termsChecked, setTermsChecked] = useState(false);
  const [birthday, setBirthday] = useState<Date | null>(null);
  const [weightInput, setWeightInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inputTheme = {
    colors: {
      onSurfaceVariant: colors.textSecondary,
      surface: colors.card,
    },
  };

  const handleRegister = () => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = t('auth.firstNameRequired');
    if (!lastName.trim()) newErrors.lastName = t('auth.lastNameRequired');
    if (!email.trim()) newErrors.email = t('auth.emailRequired');
    if (!password.trim()) newErrors.password = t('auth.passwordRequired');
    if (!confirmPassword.trim()) newErrors.confirmPassword = t('auth.passwordRequired');
    if (password !== confirmPassword) newErrors.confirmPassword = t('auth.passwordMismatch');
    if (!birthday) newErrors.dob = t('auth.dateOfBirthRequired');
    if (!termsChecked) newErrors.terms = t('auth.termsRequired');
    if (birthday && !isAdult(birthday)) newErrors.dob = t('auth.ageRequirement');
    // Weight/height are optional, but validate format if provided
    if (weightInput && isNaN(parseFloat(weightInput))) newErrors.weight = t('auth.invalidNumber');
    if (heightInput && isNaN(parseFloat(heightInput))) newErrors.height = t('auth.invalidNumber');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    
    // Format birthday as YYYY-MM-DD
    const birthdayStr = birthday ? birthday.toISOString().split('T')[0] : undefined;

    // Convert weight/height to canonical units for passing to EmailVerification
    const wVal = parseFloat(weightInput);
    const hVal = parseFloat(heightInput);
    const weightKg = !isNaN(wVal) ? weightToKg(wVal, unitSystem) : null;
    const heightCm = !isNaN(hVal) ? heightToCm(hVal, unitSystem) : null;

    registerMutation.mutate(
      {
        email: email.trim(),
        password,
        name: `${firstName.trim()} ${lastName.trim()}`,
        role,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      },
      {
        onSuccess: (result) => {
          if (result.emailVerified) {
            // Already verified (unlikely but handle) — sync and go to app
            navigation.navigate('AuthCallback');
          } else {
            navigation.navigate('EmailVerification', {
              email: result.email,
              role: result.role,
              first_name: result.first_name,
              last_name: result.last_name,
              birthday: birthdayStr,
              terms_accepted: true,
              weight_kg: weightKg,
              height_cm: heightCm,
              measurement_unit: unitSystem,
            });
          }
        },
      },
    );
  };

  return (
    <AuthLayout
      title={t('auth.createAccount')}
      subtitle={t('auth.joinCbjj')}
      useKeyboardAvoiding
    >
          <GoogleSignInButton />

          <AppleSignInButton />

          <AuthDivider />

          <View style={styles.row}>
            <PaperTextInput
              label={t('auth.firstName')}
              value={firstName}
              onChangeText={(v) => { setFirstName(v); if (errors.firstName) setErrors((e) => ({ ...e, firstName: '' })); }}
              mode="outlined"
              error={!!errors.firstName}
              style={[styles.input, styles.halfInput]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
              theme={inputTheme}
            />
            <PaperTextInput
              label={t('auth.lastName')}
              value={lastName}
              onChangeText={(v) => { setLastName(v); if (errors.lastName) setErrors((e) => ({ ...e, lastName: '' })); }}
              mode="outlined"
              error={!!errors.lastName}
              style={[styles.input, styles.halfInput]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
              theme={inputTheme}
            />
          </View>
          {(errors.firstName || errors.lastName) && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.firstName || errors.lastName}
            </Text>
          )}

          <PaperTextInput
            label={t('auth.email')}
            value={email}
            onChangeText={(v) => { setEmail(v); if (errors.email) setErrors((e) => ({ ...e, email: '' })); }}
            mode="outlined"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            error={!!errors.email}
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.text}
            textColor={colors.text}
            theme={inputTheme}
          />
          {errors.email && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.email}
            </Text>
          )}

          <PaperTextInput
            label={t('auth.password')}
            value={password}
            onChangeText={(v) => { setPassword(v); if (errors.password) setErrors((e) => ({ ...e, password: '' })); }}
            mode="outlined"
            secureTextEntry={secureEntry}
            error={!!errors.password}
            right={
              <PaperTextInput.Icon
                icon={secureEntry ? 'eye-off' : 'eye'}
                onPress={() => setSecureEntry(!secureEntry)}
                color={colors.textTertiary}
              />
            }
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.text}
            textColor={colors.text}
            theme={inputTheme}
          />
          {errors.password && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.password}
            </Text>
          )}

          <PaperTextInput
            label={t('auth.confirmPassword')}
            value={confirmPassword}
            onChangeText={(v) => { setConfirmPassword(v); if (errors.confirmPassword) setErrors((e) => ({ ...e, confirmPassword: '' })); }}
            mode="outlined"
            secureTextEntry={secureEntryConfirm}
            error={!!errors.confirmPassword}
            right={
              <PaperTextInput.Icon
                icon={secureEntryConfirm ? 'eye-off' : 'eye'}
                onPress={() => setSecureEntryConfirm(!secureEntryConfirm)}
                color={colors.textTertiary}
              />
            }
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.text}
            textColor={colors.text}
            theme={inputTheme}
          />
          {errors.confirmPassword && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.confirmPassword}
            </Text>
          )}

          <DatePicker
            label={t('auth.dateOfBirth')}
            value={birthday}
            onChange={(date) => {
              setBirthday(date);
              if (errors.dob) setErrors((e) => ({ ...e, dob: '' }));
            }}
            error={!!errors.dob}
            maximumDate={new Date()}
            minimumDate={new Date(1900, 0, 1)}
          />
          {errors.dob && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.dob}
            </Text>
          )}

          {/* Body Measurements (optional) */}
          <Text style={[styles.fieldHint, { color: colors.textSecondary }]}>
            {t('profile.bodyMeasurementsHint')}
          </Text>
          <View style={styles.row}>
            <PaperTextInput
              label={`${t('profile.weight')} (${unitSystem === 'metric' ? 'kg' : 'lbs'})`}
              value={weightInput}
              onChangeText={(v) => { setWeightInput(v); if (errors.weight) setErrors((e) => ({ ...e, weight: '' })); }}
              mode="outlined"
              error={!!errors.weight}
              keyboardType="decimal-pad"
              style={[styles.input, { flex: 1 }]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
              theme={inputTheme}
            />
            <PaperTextInput
              label={`${t('profile.height')} (${unitSystem === 'metric' ? 'cm' : 'in'})`}
              value={heightInput}
              onChangeText={(v) => { setHeightInput(v); if (errors.height) setErrors((e) => ({ ...e, height: '' })); }}
              mode="outlined"
              error={!!errors.height}
              keyboardType="decimal-pad"
              style={[styles.input, { flex: 1 }]}
              outlineColor={colors.border}
              activeOutlineColor={colors.text}
              textColor={colors.text}
              theme={inputTheme}
            />
            <TouchableOpacity
              style={[styles.unitToggleBtn, { alignSelf: 'center' }]}
              onPress={() => setUnitSystem(unitSystem === 'metric' ? 'imperial' : 'metric')}
            >
              <Text style={[styles.unitToggleText, { color: colors.accent }]}>
                {unitSystem === 'metric' ? t('profile.metric') : t('profile.imperial')}
              </Text>
            </TouchableOpacity>
          </View>
          {(errors.weight || errors.height) && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.weight || errors.height}
            </Text>
          )}

          <View style={styles.termsRow}>
            {Platform.OS === 'ios' ? (
              <Switch
                value={termsChecked}
                onValueChange={(v) => {
                  setTermsChecked(v);
                  if (errors.terms) setErrors((e) => ({ ...e, terms: '' }));
                }}
                trackColor={{ false: colors.border, true: colors.accent }}
                thumbColor={termsChecked ? colors.accentText : colors.textSecondary}
                style={{ marginRight: 8 }}
              />
            ) : (
              <Checkbox
                status={termsChecked ? 'checked' : 'unchecked'}
                onPress={() => {
                  setTermsChecked(!termsChecked);
                  if (errors.terms) setErrors((e) => ({ ...e, terms: '' }));
                }}
                color={colors.accent}
              />
            )}
            <Text style={[styles.termsText, { color: colors.text }]}>
              {t('auth.termsPrefix')}{' '}
              <Text
                style={[styles.termsLink, { color: colors.accent }]}
                onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'privacy_policy' })}
              >
                {t('auth.privacyPolicy')}
              </Text>
              {' '}{t('auth.termsAnd')}{' '}
              <Text
                style={[styles.termsLink, { color: colors.accent }]}
                onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'terms_of_service' })}
              >
                {t('auth.termsOfService')}
              </Text>
            </Text>
          </View>
          {errors.terms && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.terms}
            </Text>
          )}

          {registerMutation.isError && (
            <Text style={[styles.error, { color: colors.error }]}>
              {(registerMutation.error as any)?.code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL'
                ? t('errors.email_already_in_use')
                : getApiError(registerMutation.error, t('auth.registrationFailed'), t)}
            </Text>
          )}

          <TouchableOpacity
            onPress={handleRegister}
            disabled={registerMutation.isPending}
            style={[styles.button, { backgroundColor: colors.accent }]}
            activeOpacity={0.8}
          >
            {registerMutation.isPending ? (
              <ActivityIndicator size="small" color={colors.accentText} />
            ) : (
              <Text style={[styles.buttonText, { color: colors.accentText }]}>
                {t('auth.register')}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.linkButton}
            activeOpacity={0.7}
          >
            <Text style={[styles.linkText, { color: colors.textSecondary }]}>
              {t('auth.alreadyHaveAccount')}{' '}
              <Text style={{ color: colors.text, fontWeight: '600' }}>
                {t('auth.signInLink')}
              </Text>
            </Text>
          </TouchableOpacity>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  input: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  halfInput: {
    flex: 1,
  },
  error: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 13,
  },
  fieldError: {
    marginTop: -8,
    marginBottom: 8,
    marginLeft: 4,
    fontSize: 12,
  },
  fieldHint: {
    fontSize: 12,
    marginBottom: 8,
    marginLeft: 4,
  },
  button: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
  },
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 8,
    marginBottom: 8,
  },
  termsText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    paddingTop: 6,
  },
  termsLink: {
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  unitToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  unitToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
